import { useEffect, useRef, useState } from "react";
import { ProfileViewsChart } from "../ProfileViewsChart";
import { BreederSpecies, BusinessTypes } from "./SpecialtyChips";
import { StoreMap, ShopOnline, OpenBadge } from "./StoreMap";
import { LitterBoard } from "./LitterBoard";
import { FollowBreederButton } from "./FollowBreederButton";
import { useLanguage } from "../../context/LanguageContext";
import { ReviewText } from "./ReviewText";
import { JourneyTip } from "./JourneyTip";
import { Link, useLocation } from "react-router-dom";
import { api, photoUrl, errText, sellerShareUrl } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { useAuth } from "../../context/AuthContext";
import { ProfileText } from "../ProfileText";
import { ListingCard } from "../ListingCard";
import { StarRating } from "../StarRating";
import { StaffTag } from "../RoleAvatar";
import { ReportUserDialog } from "../ReportUserDialog";
import { CropDialog } from "../CropDialog";
import { BannerTemplateDialog } from "./BannerTemplateDialog";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { CityInput } from "../CityInput";
import { formatLocation } from "../../lib/cities";
import {
  BadgeCheck, Calendar, Package, Star, MessageSquarePlus, Flag, MapPin, Clock, Globe, Camera,
  Loader2, Pencil, Images, Plus, X, ChevronLeft, ChevronRight, Instagram, Facebook, Youtube,
  Sparkles, Store, Mail, Send, ExternalLink, Eye, Share2, Move, Check, QrCode, Download, Smartphone,
  GripVertical, ArrowUp, ArrowDown,
  Egg, AlertTriangle, LayoutTemplate, CalendarX,
} from "lucide-react";
import { ClosuresEditor } from "./ClosuresEditor";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const HOURS_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const SOCIALS = [
  { key: "instagram", label: "Instagram", Icon: Instagram, color: "#E1306C" },
  { key: "facebook", label: "Facebook", Icon: Facebook, color: "#1877F2" },
  { key: "tiktok", label: "TikTok", Icon: TikTokIcon, color: "#111111" },
  { key: "youtube", label: "YouTube", Icon: Youtube, color: "#FF0000" },
];

function TikTokIcon({ size = 18, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.1v12.4a2.59 2.59 0 1 1-2.6-2.6c.27 0 .53.04.78.12V9.77a5.9 5.9 0 0 0-.78-.05 5.69 5.69 0 1 0 5.69 5.69V9.01a7.35 7.35 0 0 0 4.3 1.38V7.28a4.28 4.28 0 0 1-3.23-1.46Z" />
    </svg>
  );
}

function hasHours(hours) {
  return hours && HOURS_ORDER.some((d) => hours[d] && !hours[d].closed && hours[d].open && hours[d].close);
}

function openStatus(hours) {
  if (!hasHours(hours)) return null;
  const now = new Date();
  const d = hours[DAY_KEYS[now.getDay()]];
  if (!d || d.closed || !d.open || !d.close) return { open: false };
  const [oh, om] = d.open.split(":").map(Number);
  const [ch, cm] = d.close.split(":").map(Number);
  const cur = now.getHours() * 60 + now.getMinutes();
  return { open: cur >= oh * 60 + om && cur <= ch * 60 + cm, close: d.close, openT: d.open };
}

function timeAgoShort(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "moments ago";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

const NAV = [
  { id: "about", label: "About" },
  { id: "gallery", label: "Gallery" },
  { id: "listings", label: "Listings" },
  { id: "reviews", label: "Reviews" },
  { id: "contact", label: "Contact" },
];

export default function VerifiedProfile({ data, mine, onReload }) {
  const { user, setUser, setAuthOpen } = useAuth();
  const isAdmin = user?.role === "admin";
  const { t, lang } = useLanguage();
  const { hash } = useLocation();
  useEffect(() => {
    const el = hash && sectionRefs.current[hash.slice(1)];
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
  }, [hash, data]);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [active, setActive] = useState("about");
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverCrop, setCoverCrop] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [coverDims, setCoverDims] = useState(null);
  const coverOff = coverDims && Math.abs(coverDims[0] / coverDims[1] - 100 / 13) / (100 / 13) > 0.15;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [lightbox, setLightbox] = useState(-1);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [coverPos, setCoverPos] = useState(data.cover_position ?? 50);
  const [repositioning, setRepositioning] = useState(false);
  const [savingPos, setSavingPos] = useState(false);
  const coverRef = useRef(null);
  const sectionRefs = useRef({});
  const nav = data.hosts_litters ? [...NAV.slice(0, 2), { id: "litters", label: t("lt_nav") }, ...NAV.slice(2)] : NAV;
  const dragState = useRef(null);

  useEffect(() => { setCoverPos(data.cover_position ?? 50); }, [data.cover_position]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) setActive(vis[0].target.id);
      },
      { rootMargin: "-120px 0px -55% 0px", threshold: [0.1, 0.5] }
    );
    nav.forEach((n) => { const el = sectionRefs.current[n.id]; if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) { setActive(id); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
  };

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (avatarRef.current) avatarRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 25 * 1024 * 1024) return toast.error("Image must be under 25MB");
    try {
      const small = await compressImage(file, { maxDim: 1600, quality: 0.85 });
      setCropSrc(URL.createObjectURL(small));
    } catch (err) { toast.error("Could not read that image"); }
  };

  const uploadAvatar = async (blob) => {
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: fresh } = await api.put("/auth/me/profile-page", { picture: up.path });
      setUser?.((u) => (u ? { ...u, picture: fresh.picture } : u));
      closeCrop();
      toast.success("Profile photo updated");
      onReload();
    } catch (err) { toast.error(errText(err)); }
    finally { setAvatarUploading(false); }
  };

  const closeCrop = () => { if (cropSrc) URL.revokeObjectURL(cropSrc); setCropSrc(null); };

  const onPickCover = (e) => {
    const file = e.target.files?.[0];
    if (coverRef.current) coverRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 25 * 1024 * 1024) return toast.error("Image must be under 25MB");
    setCoverCrop({ file, src: URL.createObjectURL(file) });
  };
  const closeCoverCrop = () => { if (coverCrop) URL.revokeObjectURL(coverCrop.src); setCoverCrop(null); };

  const saveCover = async (blob, file) => {
    setCoverUploading(true);
    try {
      const dims = file ? await new Promise((res) => { const im = new Image(); im.onload = () => res([im.naturalWidth, im.naturalHeight]); im.onerror = () => res(null); im.src = coverCrop.src; }) : null;
      const fd = new FormData();
      fd.append("file", file ? await compressImage(file, { maxDim: 2000, quality: 0.82 }) : new File([blob], "banner.jpg", { type: "image/jpeg" }));
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/auth/me/profile-page", { cover_image: up.path });
      toast.success("Cover photo updated");
      const ratio = dims && dims[0] / dims[1];
      if (ratio && Math.abs(ratio - 100 / 13) / (100 / 13) > 0.15) {
        toast.warning(`Your banner is ${dims[0]} × ${dims[1]} px, so it won't fill the frame and dark bars will show ${ratio < 100 / 13 ? "on the sides" : "above and below"}. For a perfect fit, use 2000 × 260 px.`, { duration: 9000, id: "cover-size-warning" });
      } else if (dims && dims[0] < 1200) {
        toast.warning(`Your banner is only ${dims[0]} px wide and may look blurry on large screens. 2000 × 260 px works best.`, { duration: 9000, id: "cover-size-warning" });
      }
      closeCoverCrop();
      onReload();
    } catch (err) { toast.error(errText(err)); }
    finally { setCoverUploading(false); }
  };

  const openReview = async () => {
    if (!user) return setAuthOpen(true);
    if (data.verified_breeder) {
      try {
        const { data: el } = await api.get(`/users/${data.user_id}/review-eligibility`);
        if (!el.can_review) return toast.info(t("rv_family_only"));
      } catch (e) { return toast.error(errText(e)); }
    }
    setShowReview(!showReview);
  };

  const submitReview = async () => {
    if (!user) return setAuthOpen(true);
    if (!rating) return toast.error(t("rv_pick"));
    try {
      await api.post(`/users/${data.user_id}/reviews`, { rating, comment });
      toast.success(t("rv_done"));
      setShowReview(false); setRating(0); setComment("");
      onReload();
    } catch (e) { toast.error(errText(e)); }
  };

  const onPointerDown = (e) => {
    if (!repositioning) return;
    dragState.current = { startY: e.clientY, startPos: coverPos, h: e.currentTarget.offsetHeight || 1 };
  };
  const onPointerMove = (e) => {
    if (!repositioning || !dragState.current) return;
    const { startY, startPos, h } = dragState.current;
    const delta = ((e.clientY - startY) / h) * 100;
    setCoverPos(Math.max(0, Math.min(100, startPos - delta)));
  };
  const onPointerUp = () => { dragState.current = null; };

  const saveCoverPos = async () => {
    setSavingPos(true);
    try {
      await api.put("/auth/me/profile-page", { cover_position: Math.round(coverPos) });
      toast.success("Cover position saved");
      setRepositioning(false);
      onReload();
    } catch (e) { toast.error(errText(e)); }
    finally { setSavingPos(false); }
  };

  const shareUrl = sellerShareUrl(data);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrColor, setQrColor] = useState(data.qr_color || "#12234A");
  const [qrBust, setQrBust] = useState(0);
  const [qrLogo, setQrLogo] = useState(data.qr_logo || "");
  const [qrLogoUploading, setQrLogoUploading] = useState(false);
  const qrLogoRef = useRef(null);
  const qrUrl = `${process.env.REACT_APP_BACKEND_URL}/api/users/${data.user_id}/qr.png${qrBust ? `?v=${qrBust}` : ""}`;
  const setQrBrandColor = async (c) => {
    setQrColor(c);
    try { await api.put("/auth/me/profile-page", { qr_color: c }); setQrBust(Date.now()); onReload(); }
    catch (e) { toast.error(errText(e)); }
  };
  const onPickQrLogo = async (e) => {
    const file = e.target.files?.[0];
    if (qrLogoRef.current) qrLogoRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 25 * 1024 * 1024) return toast.error("Logo must be under 25MB");
    setQrLogoUploading(true);
    try {
      const compressed = await compressImage(file, { maxDim: 600, quality: 0.9 });
      const fd = new FormData();
      fd.append("file", compressed);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/auth/me/profile-page", { qr_logo: up.path });
      setQrLogo(up.path); setQrBust(Date.now()); onReload();
      toast.success("QR logo updated");
    } catch (err) { toast.error(errText(err)); }
    finally { setQrLogoUploading(false); }
  };
  const removeQrLogo = async () => {
    try { await api.put("/auth/me/profile-page", { qr_logo: "" }); setQrLogo(""); setQrBust(Date.now()); onReload(); toast.success("Reverted to your avatar"); }
    catch (e) { toast.error(errText(e)); }
  };
  const onShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${data.name} · CritterPosts.ca`, url: shareUrl }); return; }
      catch (e) { if (e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Share link copied to clipboard!"); }
    catch { toast.error(`Copy failed — link: ${shareUrl}`); }
  };

  const year = data.created_at ? new Date(data.created_at).getFullYear() : "—";
  const gallery = [...(data.gallery || [])].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
  const specialties = data.specialties || [];
  const socials = data.socials || {};
  const socialList = SOCIALS.filter((s) => socials[s.key]);
  const status = data.open_now != null ? { open: data.open_now } : openStatus(data.hours);
  const bodyText = data.about || data.bio || "";

  return (
    <div className="bg-[#F8FAFC]">
      {/* COVER */}
      <div
        className={`relative w-full max-w-[2000px] mx-auto aspect-[100/13] overflow-hidden bg-slate-900 ${data.cover_image ? "" : "min-h-[160px] sm:min-h-[220px]"}`}
        data-testid="profile-cover-banner"
      >
        {data.cover_image
          ? <img src={photoUrl(data.cover_image)} alt="cover" draggable={false} className="w-full h-full object-contain pointer-events-none" data-testid="profile-cover-img"
              onLoad={(e) => setCoverDims([e.currentTarget.naturalWidth, e.currentTarget.naturalHeight])} />
          : <div className="w-full h-full bg-gradient-to-br from-[#12234A] via-[#1E3A75] to-[#12234A]" />}
        {!data.cover_image && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />}
        {mine && !repositioning && (
          <>
            {!data.cover_image && (
              <button onClick={() => coverRef.current?.click()} disabled={coverUploading} data-testid="add-cover-cta"
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/90 hover:text-white group">
                <span className="flex items-center justify-center h-14 w-14 rounded-full bg-white/10 group-hover:bg-white/20 border-2 border-dashed border-white/40 backdrop-blur-sm transition-colors">
                  {coverUploading ? <Loader2 size={22} className="animate-spin" /> : <Camera size={22} />}
                </span>
                <span className="text-sm font-semibold">Add a cover photo</span>
                <span className="text-xs text-white/60">Upload a banner to brand your profile (best size 2000 × 260 px)</span>
              </button>
            )}
            {data.cover_image && (
              <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 z-10 flex gap-2">
                {coverOff && (
                  <span data-testid="cover-size-hint" title="Upload a 2000 × 260 px image so it fills the frame"
                    className="hidden sm:inline-flex items-center gap-1.5 bg-amber-50/95 text-amber-800 border border-amber-200 shadow-lg px-3 py-2 rounded-xl text-xs font-semibold">
                    <AlertTriangle size={14} /> {coverDims[0]} × {coverDims[1]} px · best fit is 2000 × 260 px
                  </span>
                )}
                <button onClick={() => setTplOpen(true)} data-testid="cover-template-button"
                  className="bg-white/95 hover:bg-white text-[#12234A] shadow-lg px-3.5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors">
                  <LayoutTemplate size={15} /> <span className="hidden sm:inline">Templates</span>
                </button>
                <button onClick={() => coverRef.current?.click()} disabled={coverUploading} data-testid="edit-cover-banner-button"
                  className="bg-white/95 hover:bg-white text-[#12234A] shadow-lg px-3.5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors">
                  {coverUploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} Change
                </button>
              </div>
            )}
            {!data.cover_image && (
              <button onClick={() => setTplOpen(true)} data-testid="cover-template-button"
                className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 z-10 bg-white/95 hover:bg-white text-[#12234A] shadow-lg px-3.5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors">
                <LayoutTemplate size={15} /> Use a template
              </button>
            )}
            <input ref={coverRef} type="file" accept="image/*" onChange={onPickCover} className="hidden" data-testid="cover-file-input" />
            <CropDialog open={!!coverCrop} src={coverCrop?.src} onClose={closeCoverCrop} aspect={100 / 13} round={false} outW={2000} outH={260} wide minZoom={0.3}
              title="Fit your banner" description="Drag and zoom to choose the part that shows. Banners are 2000 × 260 px."
              saveLabel="Save banner" onCropped={(b) => saveCover(b)} onSkip={() => saveCover(null, coverCrop.file)} skipLabel="Use whole image" />
            {tplOpen && <BannerTemplateDialog open={tplOpen} onClose={() => setTplOpen(false)} data={data} onSaved={onReload} />}
          </>
        )}
        {mine && repositioning && (
          <>
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-black/70 text-white text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5 pointer-events-none">
              <Move size={13} /> Drag the image up or down to reposition
            </div>
            <div className="absolute bottom-4 right-4 z-10 flex gap-2">
              <button onClick={() => { setCoverPos(data.cover_position ?? 50); setRepositioning(false); }} data-testid="cover-reposition-cancel"
                className="bg-white/95 hover:bg-white text-slate-700 shadow-lg px-3.5 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5"><X size={15} /> Cancel</button>
              <button onClick={saveCoverPos} disabled={savingPos} data-testid="cover-reposition-save"
                className="bg-[#F5551E] hover:bg-[#E04812] text-white shadow-lg px-3.5 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5">{savingPos ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save position</button>
            </div>
          </>
        )}
      </div>

      {/* HEADER */}
      <div className="relative z-20 px-4 sm:px-8 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 mt-6">
          <div className="relative shrink-0 group">
            <div className="h-28 w-28 sm:h-36 sm:w-36 rounded-full border-4 border-white shadow-xl overflow-hidden bg-slate-100">
              <Avatar className="h-full w-full rounded-none">
                <AvatarImage src={photoUrl(data.picture)} className="object-cover" data-testid="profile-avatar-img" />
                <AvatarFallback className="rounded-none bg-[#12234A] text-white text-3xl">{data.name?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
            </div>
            {mine && (
              <>
                <button type="button" onClick={() => avatarRef.current?.click()} disabled={avatarUploading} data-testid="change-avatar-btn" title="Change photo"
                  className="absolute bottom-1 right-1 h-10 w-10 rounded-full bg-[#F5551E] hover:bg-[#E04812] text-white flex items-center justify-center border-4 border-white shadow-lg transition-transform hover:scale-105">
                  {avatarUploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                </button>
                <input ref={avatarRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" data-testid="avatar-file-input" />
                <CropDialog src={cropSrc} open={!!cropSrc} onClose={closeCrop} onCropped={uploadAvatar} />
              </>
            )}
          </div>
          <div className="flex-1 min-w-0 sm:pt-[76px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#12234A] tracking-tight" data-testid="seller-name">{data.name}</h1>
              <StaffTag role={data.role} />
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              {data.verified_breeder && <span className="inline-flex items-center gap-1.5 bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm" data-testid="verified-badge-profile-breeder"><BadgeCheck size={14} /> Verified Breeder</span>}
              {data.verified_business && <span className="inline-flex items-center gap-1.5 bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm" data-testid="verified-badge-profile-business"><BadgeCheck size={14} /> Verified Business</span>}
              {(data.verified_seller || (!data.verified_business && data.is_verified_seller)) && <span className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm" data-testid="verified-badge-profile"><BadgeCheck size={14} /> Verified Seller</span>}
              <span className="inline-flex items-center gap-1 bg-slate-100 text-[#12234A] text-xs font-semibold px-2.5 py-1 rounded-full"><Store size={12} className="text-[#F5551E]" /> {data.seller_type}</span>
              <OpenBadge open={data.open_now} closure={data.closed_today} />
              {data.presence === "active" && <span data-testid="profile-presence" className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Active now</span>}
              {data.presence === "away" && <span data-testid="profile-presence" className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Away</span>}
            </div>
          </div>
          {mine ? (
            <div className="flex gap-2 sm:pt-[76px] flex-wrap sm:justify-end sm:max-w-[440px]">
              <Button onClick={onShare} data-testid="share-profile-btn" variant="outline" className="rounded-xl gap-1.5 shadow-sm"><Share2 size={15} /> Share</Button>
              <Button onClick={() => setQrOpen(true)} data-testid="qr-profile-btn" variant="outline" className="rounded-xl gap-1.5 shadow-sm"><QrCode size={15} /> QR</Button>
              <Button asChild variant="outline" className="rounded-xl gap-1.5 shadow-sm">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/users/${data.user_id}/story.png`} download data-testid="story-image-btn" title="Download a tall image for Instagram & Facebook Stories"><Smartphone size={15} /> Story image</a>
              </Button>
              <Button onClick={() => setDetailsOpen(true)} data-testid="edit-profile-details-btn" variant="outline" className="rounded-xl gap-1.5 shadow-sm"><Pencil size={15} /> Edit details</Button>
              <Button onClick={() => setGalleryOpen(true)} data-testid="manage-gallery-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5 shadow-sm"><Images size={15} /> Photos</Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2 shrink-0 sm:pt-[76px]">
              <div className="flex gap-2">
                <Button onClick={openReview} data-testid="write-review-btn" variant="outline" className="rounded-xl gap-2 shadow-sm flex-1"><MessageSquarePlus size={16} /> {t("rv_write")}</Button>
                <Button onClick={onShare} data-testid="share-profile-btn" variant="outline" className="rounded-xl shadow-sm px-3"><Share2 size={16} /></Button>
                <Button onClick={() => setQrOpen(true)} data-testid="qr-profile-btn" variant="outline" className="rounded-xl shadow-sm px-3"><QrCode size={16} /></Button>
              </div>
              {data.role === "user" && <ReportUserDialog userId={data.user_id} userName={data.name} trigger={<button data-testid="report-seller-btn" className="inline-flex items-center justify-center gap-1.5 text-sm text-slate-400 hover:text-amber-600 transition-colors cursor-pointer"><Flag size={14} /> Report</button>} />}
            </div>
          )}
        </div>
        {data.tagline && <p className="mt-3 text-sm italic text-slate-600 max-w-2xl">{data.tagline}</p>}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm text-slate-500">
          {data.location && <span className="inline-flex items-center gap-1.5"><MapPin size={15} className="text-[#F5551E]" /> {data.location}</span>}
          {data.year_established && <span className="inline-flex items-center gap-1.5"><Calendar size={15} className="text-[#F5551E]" /> Est. {data.year_established}</span>}
          <span className="inline-flex items-center gap-1.5"><Star size={15} className="text-[#F5551E]" /> {data.review_count ? `${data.avg_rating} (${data.review_count})` : "No reviews yet"}</span>
          <span className="inline-flex items-center gap-1.5"><Package size={15} className="text-[#F5551E]" /> {data.active_listings} active listing{data.active_listings === 1 ? "" : "s"}</span>
          <span className="inline-flex items-center gap-1.5"><Clock size={15} className="text-slate-400" /> Member since {year}</span>
          {isAdmin && <span className="inline-flex items-center gap-1.5 text-[#12234A] font-medium" data-testid="profile-views-stat"><Eye size={15} className="text-[#F5551E]" /> {(data.profile_views || 0).toLocaleString()} profile view{data.profile_views === 1 ? "" : "s"} <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">admin only</span></span>}
        </div>
        <BreederSpecies data={data} mine={mine} onReload={onReload} />
        <BusinessTypes data={data} mine={mine} onReload={onReload} />
        <ShopOnline data={data} />
        {isAdmin && <div className="mt-4 max-w-md"><ProfileViewsChart compact userId={data.user_id} /></div>}
      </div>

      {/* STICKY NAV */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-md border-y border-slate-200/80 shadow-sm mt-5" data-testid="profile-sticky-nav">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar max-w-5xl mx-auto px-4 sm:px-8 py-2.5">
          {nav.map((n) => (
            <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`nav-tab-${n.id}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${active === n.id ? "bg-[#12234A] text-white font-semibold shadow-sm" : "text-slate-600 hover:text-[#12234A] hover:bg-slate-100"}`}>
              {n.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-16">
        {showReview && !mine && (
          <div className="mt-6 bg-white rounded-2xl p-5 border border-slate-200/80">
            <StarRating value={rating} onChange={setRating} size={26} />
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" className="mt-3 rounded-xl" rows={3} placeholder={t("rv_ph")} />
            <Button onClick={submitReview} data-testid="submit-review-btn" className="mt-3 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white">{t("rv_submit")}</Button>
          </div>
        )}

        {/* ABOUT */}
        <section id="about" ref={(el) => (sectionRefs.current.about = el)} className="scroll-mt-32 pt-8">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-[#12234A] mb-4 flex items-center gap-2"><Sparkles size={20} className="text-[#F5551E]" /> About</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
                {bodyText
                  ? <ProfileText userId={data.user_id} field={data.about ? "about" : "bio"} text={bodyText} className="text-slate-700 leading-relaxed whitespace-pre-wrap" testid="profile-about" />
                  : <p className="text-slate-400 italic">{mine ? "Tell buyers your story — tap “Edit details” to add an about section." : "This member hasn't added an about section yet."}</p>}
                {data.service_area && (
                  <p className="mt-4 text-sm text-slate-600 flex items-center gap-2"><MapPin size={15} className="text-[#F5551E] shrink-0" /> <span><span className="font-semibold text-[#12234A]">Service area:</span> {data.service_area}</span></p>
                )}
              </div>
              {specialties.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
                  <h3 className="text-sm font-bold text-[#12234A] mb-3 uppercase tracking-wide">Specialties &amp; services</h3>
                  <div className="flex flex-wrap gap-2" data-testid="profile-specialties">
                    {specialties.map((s, i) => <span key={i} className="inline-flex items-center gap-1 bg-[#FFF4F0] text-[#F5551E] text-xs font-semibold px-3 py-1.5 rounded-full border border-orange-100">{s}</span>)}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-5">
              <StoreMap data={data} />
              {hasHours(data.hours) && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="business-hours-card">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-[#12234A] uppercase tracking-wide flex items-center gap-1.5"><Clock size={15} className="text-[#F5551E]" /> Hours</h3>
                    {data.closed_today ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Closed today</span>
                      : status && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${status.open ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{status.open ? "Open now" : "Closed"}</span>}
                  </div>
                  <div className="space-y-1.5">
                    {HOURS_ORDER.map((d) => {
                      const h = data.hours[d] || {};
                      const today = DAY_KEYS[new Date().getDay()] === d;
                      return (
                        <div key={d} className={`flex items-center justify-between text-sm ${today ? "font-semibold text-[#12234A]" : "text-slate-600"}`}>
                          <span>{DAY_LABELS[d]}</span>
                          <span>{h.closed || !h.open || !h.close ? <span className="text-slate-400">Closed</span> : `${h.open} – ${h.close}`}</span>
                        </div>
                      );
                    })}
                  </div>
                  {data.special_closures?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100" data-testid="special-closures-list">
                      <p className="text-xs font-bold uppercase tracking-wide text-red-600 flex items-center gap-1.5 mb-1.5"><CalendarX size={13} /> Special closures</p>
                      {data.special_closures.slice(0, 5).map((c) => (
                        <div key={c.date} className="flex items-center justify-between text-sm text-slate-600" data-testid={`special-closure-${c.date}`}>
                          <span>{new Date(`${c.date}T12:00:00`).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" })}</span>
                          <span className="text-slate-400 truncate ml-3">{c.note || "Closed"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {(data.website || socialList.length > 0) && (
                <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
                  <h3 className="text-sm font-bold text-[#12234A] mb-3 uppercase tracking-wide">Find us online</h3>
                  <div className="space-y-2">
                    {data.website && <a href={data.website} target="_blank" rel="noreferrer" data-testid="profile-website-link" className="flex items-center gap-2 text-sm text-[#12234A] hover:text-[#F5551E] transition-colors font-medium"><Globe size={16} className="text-[#F5551E]" /> <span className="truncate">{data.website.replace(/^https?:\/\//, "")}</span><ExternalLink size={12} className="text-slate-400" /></a>}
                    {socialList.map((s) => (
                      <a key={s.key} href={socials[s.key]} target="_blank" rel="noreferrer" data-testid={`social-link-${s.key}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#12234A] transition-colors font-medium">
                        <s.Icon size={16} style={{ color: s.color }} /> {s.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* GALLERY */}
        <section id="gallery" ref={(el) => (sectionRefs.current.gallery = el)} className="scroll-mt-32 pt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#12234A] flex items-center gap-2"><Images size={20} className="text-[#F5551E]" /> Gallery</h2>
            {mine && <Button onClick={() => setGalleryOpen(true)} variant="outline" size="sm" className="rounded-xl gap-1.5"><Pencil size={13} /> Manage</Button>}
          </div>
          {gallery.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
              <Images size={34} className="mx-auto text-slate-300" />
              <p className="text-slate-400 mt-3 text-sm">{mine ? "Showcase your animals, facility or happy customers — add up to 10 photos." : "No photos yet."}</p>
              {mine && <Button onClick={() => setGalleryOpen(true)} className="mt-4 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5"><Plus size={15} /> Add photos</Button>}
            </div>
          ) : (
            <>
              {gallery[0].featured && (
                <button onClick={() => setLightbox(0)} data-testid="gallery-featured-hero"
                  className="relative w-full mb-4 rounded-2xl overflow-hidden border border-[#F5551E]/40 ring-1 ring-[#F5551E]/20 group aspect-[16/9] sm:aspect-[21/9] bg-slate-100 block">
                  <img src={photoUrl(gallery[0].path)} alt={gallery[0].caption || "featured"} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#F5551E] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow"><Star size={12} className="fill-white" /> Featured</span>
                  {gallery[0].caption && <span className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white text-sm font-medium text-left">{gallery[0].caption}</span>}
                </button>
              )}
              {gallery.length > (gallery[0].featured ? 1 : 0) && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="profile-gallery-grid">
                  {gallery.slice(gallery[0].featured ? 1 : 0).map((g, idx) => {
                    const i = (gallery[0].featured ? 1 : 0) + idx;
                    return (
                      <button key={i} onClick={() => setLightbox(i)} data-testid={`gallery-image-item-${i}`} className="aspect-square relative group rounded-xl overflow-hidden border border-slate-200 bg-slate-100 cursor-pointer">
                        <img src={photoUrl(g.path)} alt={g.caption || "gallery"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-out" />
                        {g.caption && <span className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 line-clamp-2 text-left">{g.caption}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>

        {/* LISTINGS */}
        {data.hosts_litters && (
          <section id="litters" ref={(el) => (sectionRefs.current.litters = el)} className="scroll-mt-32 pt-10" data-testid="profile-litters-section">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
              <h2 className="font-display text-xl sm:text-2xl font-bold text-[#12234A] flex items-center gap-2"><Egg size={20} className="text-[#F5551E]" /> {t("lt_tab")}</h2>
              <FollowBreederButton breederId={data.user_id} mine={mine} />
            </div>
            <p className="text-sm text-slate-500 mb-4">{t("lt_section_sub")}</p>
            <JourneyTip fr={lang === "fr"} />
            <LitterBoard breederId={data.user_id} mine={mine} />
          </section>
        )}

        <section id="listings" ref={(el) => (sectionRefs.current.listings = el)} className="scroll-mt-32 pt-10">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-[#12234A] mb-4 flex items-center gap-2"><Package size={20} className="text-[#F5551E]" /> Listings <span className="text-slate-400 font-medium text-base">({data.active_listings})</span></h2>
          {data.listings?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {data.listings.map((l) => <ListingCard key={l.id} listing={{ ...l, seller: { is_verified_seller: data.is_verified_seller, verified_seller: data.verified_seller, verified_business: data.verified_business, verified_breeder: data.verified_breeder, role: data.role } }} />)}
            </div>
          ) : <div className="bg-white rounded-2xl border border-slate-200/80 p-10 text-center text-slate-400">No active listings right now.</div>}
        </section>

        {/* REVIEWS */}
        <section id="reviews" ref={(el) => (sectionRefs.current.reviews = el)} className="scroll-mt-32 pt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#12234A] flex items-center gap-2"><Star size={20} className="text-[#F5551E]" /> Reviews</h2>
            {data.review_count > 0 && <div className="flex items-center gap-2"><span className="text-2xl font-extrabold text-[#12234A]">{data.avg_rating}</span><StarRating value={Math.round(data.avg_rating)} size={16} /><span className="text-sm text-slate-400">({data.review_count})</span></div>}
          </div>
          {data.reviews?.length ? (
            <div className="space-y-3">
              {data.reviews.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9"><AvatarImage src={photoUrl(r.reviewer_picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{r.reviewer_name?.[0]}</AvatarFallback></Avatar>
                      <div><p className="font-semibold text-[#12234A] text-sm flex items-center gap-1.5 flex-wrap">{r.reviewer_name}{r.verified_family && <span className="text-[10px] font-bold uppercase tracking-wide bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`review-verified-family-${r.id}`}><BadgeCheck size={11} /> {t("rv_family")}</span>}</p><p className="text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</p></div>
                    </div>
                    <StarRating value={r.rating} size={14} />
                  </div>
                  <ReviewText r={r} />
                </div>
              ))}
            </div>
          ) : <div className="bg-white rounded-2xl border border-slate-200/80 p-10 text-center text-slate-400">No reviews yet.{!mine && " Be the first to leave one!"}</div>}
        </section>

        {/* CONTACT */}
        <section id="contact" ref={(el) => (sectionRefs.current.contact = el)} className="scroll-mt-32 pt-10">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-[#12234A] mb-4 flex items-center gap-2"><Mail size={20} className="text-[#F5551E]" /> Contact</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/80">
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Interested in {data.name}? Reach out through a listing to start a secure conversation on CritterPosts, or find them online.</p>
              {data.active_listings > 0
                ? <Button onClick={() => scrollTo("listings")} data-testid="contact-message-button" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-2 w-full sm:w-auto"><Send size={16} /> Message via a listing</Button>
                : <p className="text-sm text-slate-400 italic">No active listings to message through right now.</p>}
            </div>
            <div className="space-y-2">
              {data.website && <a href={data.website} target="_blank" rel="noreferrer" data-testid="contact-website" className="flex items-center gap-2 text-sm font-medium text-[#12234A] hover:text-[#F5551E] transition-colors bg-white rounded-xl px-4 py-3 border border-slate-200"><Globe size={16} className="text-[#F5551E]" /> <span className="truncate flex-1">{data.website.replace(/^https?:\/\//, "")}</span><ExternalLink size={13} className="text-slate-400" /></a>}
              {socialList.map((s) => (
                <a key={s.key} href={socials[s.key]} target="_blank" rel="noreferrer" data-testid={`contact-social-${s.key}`} className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#12234A] transition-colors bg-white rounded-xl px-4 py-3 border border-slate-200">
                  <s.Icon size={16} style={{ color: s.color }} /> <span className="flex-1">{s.label}</span><ExternalLink size={13} className="text-slate-400" />
                </a>
              ))}
              {!data.website && socialList.length === 0 && <p className="text-sm text-slate-400 italic">No external links added.</p>}
            </div>
          </div>
        </section>
      </div>

      {lightbox >= 0 && gallery[lightbox] && (
        <GalleryLightbox items={gallery} index={lightbox} onClose={() => setLightbox(-1)} onNav={setLightbox} />
      )}
      {mine && detailsOpen && <EditDetailsDialog data={data} onClose={() => setDetailsOpen(false)} onSaved={() => { setDetailsOpen(false); onReload(); }} />}
      {mine && galleryOpen && <GalleryManagerDialog data={data} onClose={() => setGalleryOpen(false)} onSaved={() => { setGalleryOpen(false); onReload(); }} />}

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-sm bg-white" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><QrCode size={18} className="text-[#F5551E]" /> Profile QR code</DialogTitle>
            <DialogDescription className="text-slate-500">Print it on flyers, business cards or signage — scanning opens this profile on CritterPosts.ca.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <img src={qrUrl} alt="Profile QR code" className="w-56 h-auto rounded-xl border border-slate-200" data-testid="qr-image" />
            {mine && (
              <div className="w-full">
                <p className="text-xs font-semibold text-slate-500 mb-2 text-center">Match your brand colour</p>
                <div className="flex items-center justify-center gap-2 flex-wrap" data-testid="qr-color-options">
                  {["#12234A", "#F5551E", "#059669", "#7C3AED", "#DC2626", "#0EA5E9", "#DB2777", "#111827"].map((c) => (
                    <button key={c} onClick={() => setQrBrandColor(c)} data-testid={`qr-color-${c.slice(1)}`}
                      className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${qrColor.toLowerCase() === c.toLowerCase() ? "border-[#12234A] ring-2 ring-offset-1 ring-[#12234A]/40" : "border-white shadow"}`}
                      style={{ backgroundColor: c }} aria-label={c} />
                  ))}
                </div>
                <div className="flex items-center justify-center gap-3 mt-3">
                  <input ref={qrLogoRef} type="file" accept="image/*" onChange={onPickQrLogo} className="hidden" data-testid="qr-logo-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => qrLogoRef.current?.click()} disabled={qrLogoUploading} data-testid="qr-logo-upload-btn" className="rounded-xl gap-1.5 text-xs">
                    {qrLogoUploading ? <Loader2 size={13} className="animate-spin" /> : <Images size={13} />} {qrLogo ? "Change logo" : "Upload custom logo"}
                  </Button>
                  {qrLogo && <button type="button" onClick={removeQrLogo} data-testid="qr-logo-remove-btn" className="text-xs text-slate-400 hover:text-red-600 inline-flex items-center gap-1"><X size={13} /> Use avatar</button>}
                </div>
              </div>
            )}
            <a href={qrUrl} download data-testid="qr-download-btn" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold px-5 py-2.5 text-sm transition-colors w-full">
              <Download size={16} /> Download QR code
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GalleryLightbox({ items, index, onClose, onNav }) {
  const g = items[index];
  const prev = () => onNav((index - 1 + items.length) % items.length);
  const next = () => onNav((index + 1) % items.length);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") prev(); if (e.key === "ArrowRight") next(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="gallery-lightbox-modal">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white" data-testid="lightbox-close"><X size={28} /></button>
      {items.length > 1 && <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 sm:left-6 text-white/70 hover:text-white bg-white/10 rounded-full p-2"><ChevronLeft size={26} /></button>}
      <div className="max-w-4xl max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img src={photoUrl(g.path)} alt={g.caption || "photo"} className="max-h-[75vh] max-w-full rounded-xl object-contain" />
        {g.caption && <p className="text-white/90 text-sm mt-3 text-center max-w-2xl">{g.caption}</p>}
        <p className="text-white/40 text-xs mt-1">{index + 1} / {items.length}</p>
      </div>
      {items.length > 1 && <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 sm:right-6 text-white/70 hover:text-white bg-white/10 rounded-full p-2"><ChevronRight size={26} /></button>}
    </div>
  );
}

function EditDetailsDialog({ data, onClose, onSaved }) {
  const emptyHours = () => HOURS_ORDER.reduce((a, d) => ({ ...a, [d]: { closed: false, open: "09:00", close: "17:00", ...(data.hours?.[d] || {}) } }), {});
  const [form, setForm] = useState({
    about: data.about || "", location: data.location || "", service_area: data.service_area || "",
    year_established: data.year_established || "",
    store_address: data.store_address || "", shop_url: data.shop_url || "",
    special_closures: data.special_closures || [],
    specialties: data.specialties || [],
    hours: emptyHours(),
    socials: { instagram: "", facebook: "", tiktok: "", youtube: "", ...(data.socials || {}) },
  });
  const [specInput, setSpecInput] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setHour = (d, k, v) => setForm((f) => ({ ...f, hours: { ...f.hours, [d]: { ...f.hours[d], [k]: v } } }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socials: { ...f.socials, [k]: v } }));
  const addSpec = () => {
    const s = specInput.trim();
    if (s && form.specialties.length < 15 && !form.specialties.some((x) => x.toLowerCase() === s.toLowerCase())) set("specialties", [...form.specialties, s]);
    setSpecInput("");
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me/profile-page", form);
      toast.success("Profile updated");
      onSaved();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[88vh] overflow-y-auto" data-testid="edit-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Pencil size={18} className="text-[#F5551E]" /> Edit profile details</DialogTitle>
          <DialogDescription className="text-slate-500">Build out your mini web page. Everything here shows on your public profile.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-1">
          <div>
            <Label>About / your story</Label>
            <Textarea value={form.about} onChange={(e) => set("about", e.target.value)} maxLength={2500} rows={5} data-testid="edit-about" className="mt-1 rounded-xl" placeholder="Tell buyers about your operation, your values, your experience…" />
            <p className="text-[11px] text-slate-400 mt-1 text-right">{form.about.length}/2500</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>City / location</Label><div className="mt-1"><CityInput value={form.location} onChange={(v) => set("location", v)} onPick={(c) => set("location", formatLocation(c.city, c.province))} testid="edit-location" className="h-11 rounded-xl" placeholder="Toronto, ON" /></div></div>
            <div><Label>Year established</Label><Input value={form.year_established} onChange={(e) => set("year_established", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="edit-year" className="mt-1 h-11 rounded-xl" placeholder="2015" inputMode="numeric" /></div>
            <div><Label>Service area</Label><Input value={form.service_area} onChange={(e) => set("service_area", e.target.value)} data-testid="edit-service-area" className="mt-1 h-11 rounded-xl" placeholder="GTA & surrounding" /></div>
          </div>
          {(data.verified_business || data.verified_breeder) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Store address <span className="text-slate-400 font-normal">(shows a map if you pick “Store Front”)</span></Label>
                <Input value={form.store_address} onChange={(e) => set("store_address", e.target.value)} maxLength={200} data-testid="edit-store-address" className="mt-1 h-11 rounded-xl" placeholder="123 Queen St W, Toronto, ON M5H 2M9" />
              </div>
              <div>
                <Label>Online store link <span className="text-slate-400 font-normal">(for “Online Sale”)</span></Label>
                <Input value={form.shop_url} onChange={(e) => set("shop_url", e.target.value)} maxLength={300} data-testid="edit-shop-url" className="mt-1 h-11 rounded-xl" placeholder="https://shop.yourstore.ca" />
              </div>
            </div>
          )}
          <div>
            <Label>Specialties &amp; services <span className="text-slate-400 font-normal">({form.specialties.length}/15)</span></Label>
            <div className="flex gap-2 mt-1">
              <Input value={specInput} onChange={(e) => setSpecInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSpec(); } }} data-testid="edit-specialty-input" className="h-11 rounded-xl" placeholder="e.g. CKC Registered, Grooming, Vaccinated" />
              <Button type="button" onClick={addSpec} className="rounded-xl bg-[#12234A] hover:bg-[#0E1B38] text-white shrink-0"><Plus size={16} /></Button>
            </div>
            {form.specialties.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {form.specialties.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-[#FFF4F0] text-[#F5551E] text-xs font-semibold px-3 py-1.5 rounded-full border border-orange-100" data-testid={`edit-specialty-chip-${i}`}>
                    {s}<button type="button" onClick={() => set("specialties", form.specialties.filter((_, ix) => ix !== i))} className="hover:text-red-600"><X size={13} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Clock size={14} className="text-[#F5551E]" /> Business hours</Label>
            <div className="mt-2 space-y-1.5">
              {HOURS_ORDER.map((d) => (
                <div key={d} className="flex items-center gap-2 text-sm">
                  <span className="w-20 text-slate-600">{DAY_LABELS[d]}</span>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500 w-20"><input type="checkbox" checked={form.hours[d].closed} onChange={(e) => setHour(d, "closed", e.target.checked)} data-testid={`edit-hours-closed-${d}`} className="h-4 w-4 rounded accent-[#F5551E]" /> Closed</label>
                  <Input type="time" value={form.hours[d].open} disabled={form.hours[d].closed} onChange={(e) => setHour(d, "open", e.target.value)} className="h-9 rounded-lg w-32 disabled:opacity-40" />
                  <span className="text-slate-400">–</span>
                  <Input type="time" value={form.hours[d].close} disabled={form.hours[d].closed} onChange={(e) => setHour(d, "close", e.target.value)} className="h-9 rounded-lg w-32 disabled:opacity-40" />
                </div>
              ))}
            </div>
          </div>
          <ClosuresEditor value={form.special_closures} onChange={(v) => set("special_closures", v)} />
          <div>
            <Label>Social links</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
              {SOCIALS.map((s) => (
                <div key={s.key} className="relative">
                  <s.Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: s.color }} />
                  <Input value={form.socials[s.key]} onChange={(e) => setSocial(s.key, e.target.value)} data-testid={`edit-social-${s.key}`} className="pl-9 h-11 rounded-xl" placeholder={`${s.label} URL`} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-details-save" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GalleryManagerDialog({ data, onClose, onSaved }) {
  const [items, setItems] = useState(data.gallery || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef(null);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    const room = 10 - items.length;
    if (room <= 0) return toast.error("You can upload up to 10 photos");
    setUploading(true);
    try {
      const picked = files.slice(0, room);
      const uploaded = [];
      for (const f of picked) {
        if (!f.type.startsWith("image/")) continue;
        if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name} is too large`); continue; }
        const compressed = await compressImage(f, { maxDim: 1600, quality: 0.82 });
        const fd = new FormData();
        fd.append("file", compressed);
        const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ path: up.path, caption: "" });
      }
      setItems((prev) => [...prev, ...uploaded]);
      if (files.length > room) toast.info(`Added ${room} — gallery limit is 10 photos`);
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me/profile-page", { gallery: items });
      toast.success("Gallery updated");
      onSaved();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    setItems((prev) => { const a = [...prev]; const [m] = a.splice(from, 1); a.splice(to, 0, m); return a; });
  };
  const onDrop = (to) => {
    setItems((prev) => {
      if (dragIdx === null || dragIdx === to) return prev;
      const a = [...prev]; const [m] = a.splice(dragIdx, 1); a.splice(to, 0, m); return a;
    });
    setDragIdx(null);
  };
  const toggleFeatured = (i) => setItems((prev) => prev.map((x, ix) => ({ ...x, featured: ix === i ? !x.featured : false })));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[88vh] overflow-y-auto" data-testid="gallery-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Images size={18} className="text-[#F5551E]" /> Manage gallery</DialogTitle>
          <DialogDescription className="text-slate-500">Up to 10 photos. Drag to reorder, tap the star to feature one, and add captions.</DialogDescription>
        </DialogHeader>
        <div className="py-1">
          {items.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-2xl p-10 text-center">
              <Images size={32} className="mx-auto text-slate-300" />
              <p className="text-sm text-slate-400 mt-2">No photos yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((g, i) => (
                <div key={i} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                  className={`flex gap-3 items-center border rounded-xl p-2 bg-white transition-shadow ${dragIdx === i ? "opacity-50" : ""} ${g.featured ? "border-[#F5551E] ring-1 ring-[#F5551E]/30" : "border-slate-200"}`} data-testid={`gallery-edit-item-${i}`}>
                  <div className="flex flex-col items-center text-slate-300 shrink-0">
                    <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`gallery-move-up-${i}`} className="hover:text-[#12234A] disabled:opacity-30"><ArrowUp size={16} /></button>
                    <GripVertical size={16} className="cursor-grab active:cursor-grabbing" />
                    <button type="button" onClick={() => move(i, i + 1)} disabled={i === items.length - 1} data-testid={`gallery-move-down-${i}`} className="hover:text-[#12234A] disabled:opacity-30"><ArrowDown size={16} /></button>
                  </div>
                  <div className="relative shrink-0">
                    <img src={photoUrl(g.path)} alt="" className="h-20 w-20 rounded-lg object-cover bg-slate-100" draggable={false} />
                    {g.featured && <span className="absolute -top-1.5 -left-1.5 bg-[#F5551E] text-white rounded-full p-1 shadow"><Star size={11} className="fill-white" /></span>}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col gap-2">
                    <Input value={g.caption} maxLength={120} onChange={(e) => setItems((prev) => prev.map((x, ix) => ix === i ? { ...x, caption: e.target.value } : x))} data-testid={`gallery-caption-${i}`} className="h-9 rounded-lg text-sm" placeholder="Caption (optional)" />
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => toggleFeatured(i)} data-testid={`gallery-feature-${i}`} className={`text-xs inline-flex items-center gap-1 font-medium ${g.featured ? "text-[#F5551E]" : "text-slate-400 hover:text-[#F5551E]"}`}>
                        <Star size={13} className={g.featured ? "fill-[#F5551E]" : ""} /> {g.featured ? "Featured" : "Feature"}
                      </button>
                      <button type="button" onClick={() => setItems((prev) => prev.filter((_, ix) => ix !== i))} data-testid={`gallery-remove-${i}`} className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"><X size={13} /> Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPick} className="hidden" data-testid="gallery-file-input" />
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading || items.length >= 10} data-testid="gallery-add-btn" className="mt-4 rounded-xl gap-1.5 w-full">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add photos ({items.length}/10)
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={saving || uploading} data-testid="gallery-save" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save gallery</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
